import React, { useState } from "react";
import Cookies from "js-cookie";
import { Trans } from "react-i18next";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

const CookieCategories = ({ t }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedProvider, setExpandedProvider] = useState(null);

  const categories = [
    {
      name: t("cookie_banner.necessary"),
      description: t("cookie_banner.necessary_description"),
      providers: [
        {
          name: "TuuTuum AB",
          privacyPolicy: `/terms#${t("hashes.privacy")}`,
          cookies: [
            {
              name: "i18next",
              description: t(
                "cookie_banner.cookies.tuutuum.i18next.description"
              ),
              maxAge: t("cookie_banner.cookies.tuutuum.i18next.storage_time"),
              type: "HTTP-cookie",
            },
            {
              name: "_tracking_consent",
              description: t(
                "cookie_banner.cookies.tuutuum.tracking_consent.description"
              ),
              maxAge: t(
                "cookie_banner.cookies.tuutuum.tracking_consent.storage_time"
              ),
              type: "HTTP-cookie",
            },
          ],
        },
      ],
    },
    {
      name: t("cookie_banner.preferences"),
      description: t("cookie_banner.preferences_description"),
      providers: [],
    },
    {
      name: t("cookie_banner.analytics"),
      description: t("cookie_banner.analytics_description"),
      providers: [
        {
          name: "Google",
          privacyPolicy: "https://business.safety.google/privacy/",
          cookies: [
            {
              name: "_ga",
              description: t("cookie_banner.cookies.google._ga.description"),
              maxAge: t("cookie_banner.cookies.google._ga.storage_time"),
              type: "HTTP-cookie",
            },
          ],
        },
      ],
    },
    {
      name: t("cookie_banner.marketing"),
      description: t("cookie_banner.marketing_description"),
      providers: [
        {
          name: "Meta Platforms, Inc. (Facebook)",
          privacyPolicy: "https://www.facebook.com/policy.php",
          cookies: [
            {
              name: "_fbq",
              description: t("cookie_banner.cookies.meta.fbq.description"),
              maxAge: t("cookie_banner.cookies.meta.fbq.storage_time"),
              type: "HTTP-cookie",
            },
          ],
        },
      ],
    },
  ];

  const toggleAccordion = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  const toggleProviderAccordion = (provider) => {
    setExpandedProvider(expandedProvider === provider ? null : provider);
  };

  return (
    <div className="pt-3 bg-white overflow-y-auto max-h-80">
      {categories.map((category, index) => (
        <div key={index} className="border-b border-teal-200 mx-5 py-4">
          <div className="flex flex-col">
            {/* Header Row */}
            <div className="flex items-center justify-between">
              <button
                className="flex items-center text-left"
                onClick={() => toggleAccordion(category.name)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-5 w-5 transform transition-transform duration-300 ${
                    expandedCategory === category.name
                      ? "rotate-180"
                      : "rotate-0"
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                <span className="ml-2 font-semibold">
                  <Trans i18nKey={category.name} />
                </span>
              </button>
              <div>
                {category.name === t("cookie_banner.necessary") ? (
                  <span className="text-gray-500 text-xs">
                    {t("cookie_banner.required")}
                  </span>
                ) : (
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={category.enabled}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const updatedCategories = categories.map((cat) =>
                          cat.name === category.name
                            ? { ...cat, enabled: isChecked }
                            : cat
                        );
                        Cookies.set(
                          `cookieConsent_${category.name}`,
                          JSON.stringify(isChecked),
                          { expires: 365 }
                        );
                      }}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-800 rounded-full peer peer-checked:bg-purple-900 peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                )}
              </div>
            </div>
            <p className="ml-7 mt-1">
              <Trans i18nKey={category.description} />
            </p>
          </div>
          {expandedCategory === category.name && (
            <div className="mt-4 px-4 overflow-y-auto max-h-64">
              {category.providers && category.providers.length > 0 ? (
                category.providers.map((provider, idx) => (
                  <div
                    key={idx}
                    className="border border-gray-200 rounded-lg p-4 mb-4"
                  >
                    <div className="flex justify-between items-center text-left">
                      <button
                        className="flex flex-col text-left w-full space-y-2"
                        onClick={() => toggleProviderAccordion(provider.name)}
                      >
                        <div className="flex flex-row justify-between items-center">
                          <p className="font-semibold">{provider.name} </p>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-5 w-5 transform transition-transform duration-300 ${
                              expandedProvider === provider.name
                                ? "rotate-180"
                                : "rotate-0"
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                        <a
                          href={provider.privacyPolicy}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center"
                        >
                          {t("cookie_banner.cookies.privacy_link")}{" "}
                          <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-1" />
                        </a>
                      </button>
                    </div>
                    {expandedProvider === provider.name && (
                      <div className="mt-4">
                        {provider.cookies.map((cookie, cookieIdx) => (
                          <div
                            key={cookieIdx}
                            className="border border-gray-300 bg-gray-100 rounded-lg p-3 md:px-6 md:py-4 my-3"
                          >
                            <p className="font-semibold mb-1">{cookie.name}</p>
                            <p className="text-xs">{cookie.description}</p>
                            <hr className="max-w-screen-xl mx-auto my-3 border-gray-300" />
                            <div className="flex flex-col space-y-1 md:space-y-0 md:flex-row mt-2 text-xs text-left">
                              <p>
                                <span className="font-semibold">
                                  {t("cookie_banner.cookies.storage_time")}
                                </span>{" "}
                                {cookie.maxAge}
                              </p>
                              <p>
                                <span className="font-semibold md:ml-3">
                                  {t("cookie_banner.cookies.type")}
                                </span>{" "}
                                {cookie.type}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">
                  {t("cookie_banner.no_providers")}
                </p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default CookieCategories;
